import * as React from "react";
import { useMemo } from "react";
import flatten from "lodash/flatten";
import get from "lodash/get";
import styled, { css } from "styled-components";
import { getPinnedCellClassName } from "components/bootstrap/Table";
import Value from "views/components/Value";
import { AdditionalContext } from "views/logic/ActionContext";
import { parseSeries } from "views/logic/aggregationbuilder/Series";
import fieldTypeFor from "views/logic/fieldtypes/FieldTypeFor";
import useActiveQueryId from "views/hooks/useActiveQueryId";
import TableDataCell from "./TableDataCell";
import CustomHighlighting from "../highlighting/CustomHighlighting";
import DecoratedValue from "../messagelist/decoration/DecoratedValue";
const _c = (field, value, path, source) => ({ field, value, path, source });
const flattenValuePath = (valuePath) => valuePath.flatMap((path) => Object.entries(path)).map(([key, value]) => `${key}:${value}`).join("-");
const Column = ({ field, value, type, valuePath, source, unit, className = void 0 }) => {
  const additionalContextValue = useMemo(() => ({ valuePath }), [valuePath]);
  return /* @__PURE__ */ React.createElement(
    TableDataCell,
    {
      $isNumeric: type.isNumeric(),
      className,
      "data-testid": `value-cell-${flattenValuePath(valuePath)}-${field}`
    },
    /* @__PURE__ */ React.createElement(AdditionalContext.Provider, { value: additionalContextValue }, /* @__PURE__ */ React.createElement(CustomHighlighting, { field: source != null ? source : field, value }, value !== null && value !== void 0 ? /* @__PURE__ */ React.createElement(Value, { field: source != null ? source : field, type, value, unit, render: DecoratedValue }) : null))
  );
};
const fullValuePathForField = (fieldName, valuePath) => {
  const currentSeries = parseSeries(fieldName);
  return currentSeries && currentSeries.field ? [...valuePath, { _exists_: currentSeries.field }] : valuePath;
};
const columnNameToField = (column, series = []) => {
  const currentSeries = series.find((s) => s.effectiveName === column);
  return currentSeries ? currentSeries.function : column;
};
const LineNumber = styled.td(
  ({ theme }) => css`
    &&& {
      width: 20px;
      min-width: 20px;
      max-width: 200px;
      white-space: nowrap;
      text-align: right;
      color: ${theme.colors.text.secondary};
    }
  `
);
const DataTableEntry = ({
  index,
  columnPivots,
  fields,
  series,
  columnPivotValues,
  valuePath,
  item,
  showRowNumbers,
  types,
  units,
  pinnedColumnIndexes,
  striped = false
}) => {
  const classes = "message-group";
  const activeQuery = useActiveQueryId();
  const isStripedRow = striped && index % 2 !== 0;
  const fieldColumns = fields.toArray().map(
    ({ field: fieldName, source }, i) => _c(fieldName, item[fieldName], fullValuePathForField(fieldName, valuePath).slice(0, i + 1), source)
  );
  const columnPivotFields = columnPivotValues.flatMap((columnPivotValueKeys) => {
    const translatedPath = columnPivotValueKeys.flatMap((value, idx) => [columnPivots[idx], value]);
    const parentValuePath = [...valuePath];
    for (let i = 0; i < translatedPath.length; i += 2) {
      const [k, v] = translatedPath.slice(i, i + 2);
      parentValuePath.push({ [k]: v });
    }
    return series.map(({ effectiveName, function: fn }) => {
      const fullPath = [].concat(translatedPath, [effectiveName]);
      const value = get(item, fullPath);
      return _c(effectiveName, value, fullValuePathForField(fn, parentValuePath), fn);
    });
  });
  const columns = flatten([fieldColumns, columnPivotFields]);
  return /* @__PURE__ */ React.createElement("tr", { className: `fields-row ${classes}` }, showRowNumbers && /* @__PURE__ */ React.createElement(LineNumber, null, index), columns.map(({ field, value, path, source }, idx) => {
    var _a, _b;
    const key = `${activeQuery}-${field}=${value}-${idx}`;
    const nameForField = columnNameToField(field, series);
    const fieldNameForUnit = (_b = (_a = parseSeries(nameForField)) == null ? void 0 : _a.field) != null ? _b : nameForField;
    const unit = units.getFieldUnit(fieldNameForUnit);
    const columnIndex = idx + (showRowNumbers ? 1 : 0);
    const className = getPinnedCellClassName(pinnedColumnIndexes.has(columnIndex), isStripedRow);
    return /* @__PURE__ */ React.createElement(
      Column,
      {
        key,
        field,
        value,
        type: fieldTypeFor(nameForField, types),
        valuePath: path.slice(),
        source,
        unit,
        className
      }
    );
  }));
};
export default DataTableEntry;
